<template>
  <div class="formulario-modelo">
    <div class="header-flex">
      <div>
        <h1>Calendario de acreditación</h1>
        <p class="sub">Fechas regulatorias registradas por la Dirección de Calidad — el sistema las vigila y avisa.</p>
      </div>
      <button v-if="puedeGestionar" class="btn primario" @click="abrirModal()">+ Registrar evento</button>
    </div>

    <!-- Controles -->
    <div class="controles">
      <div class="tabs">
        <button class="tab" :class="{ activo: filtros.calVista === 'mes' }" @click="filtros.calVista = 'mes'">Vista mensual</button>
        <button class="tab" :class="{ activo: filtros.calVista === 'agenda' }" @click="filtros.calVista = 'agenda'">Agenda</button>
      </div>
      <div class="filtros-linea">
        <select v-model="filtros.calTipo">
          <option value="">Todos los tipos</option>
          <option v-for="t in tipos" :key="t.clave" :value="t.clave">{{ t.label }}</option>
        </select>
      </div>
    </div>

    <div v-if="cargando" class="skeleton-grupo">
      <div class="skeleton" style="height: 320px"></div>
    </div>
    <div v-else-if="error" class="aviso-error">{{ error }} <button class="btn chico" @click="cargar">Reintentar</button></div>

    <template v-else>
      <!-- ═══ Vista mensual ═══ -->
      <section v-if="filtros.calVista === 'mes'" class="tarjeta">
        <div class="mes-nav">
          <button class="btn-nav" @click="mover(-1)">‹</button>
          <h2>{{ mesLabel }}</h2>
          <button class="btn-nav" @click="mover(1)">›</button>
        </div>
        <div class="grilla-wrap">
        <div class="grilla">
          <span v-for="d in ['Lun','Mar','Mié','Jue','Vie','Sáb','Dom']" :key="d" class="dow">{{ d }}</span>
          <div v-for="(c, i) in celdas" :key="i" class="celda" :class="{ vacia: !c.fecha, hoy: c.esHoy }">
            <template v-if="c.fecha">
              <span class="num-dia">{{ c.dia }}</span>
              <button
                v-for="e in c.eventos"
                :key="e.id_evento"
                class="chip-ev"
                :class="'t-' + colorTipo(e.tipo_evento)"
                :title="`${e.tipo_label} · ${e.titulo}`"
                @click="puedeGestionar ? abrirModal(e) : verDetalle(e)"
              >{{ e.titulo }}</button>
            </template>
          </div>
        </div>
        </div>
        <div class="leyenda">
          <span v-for="(lbl, k) in LEYENDA" :key="k" class="leyenda-item"><span class="pto" :class="'t-' + k"></span>{{ lbl }}</span>
        </div>
      </section>

      <!-- ═══ Agenda ═══ -->
      <section v-else class="tarjeta">
        <!-- Los eventos pasados NO se borran, pero la agenda solo mostraba los
             que quedan por venir, así que parecía que desaparecían. -->
        <div class="agenda-tabs">
          <button :class="{ activo: !verHistorial }" @click="verHistorial = false">
            Próximos ({{ eventosFuturos.length }})
          </button>
          <button :class="{ activo: verHistorial }" @click="verHistorial = true">
            Historial ({{ eventosPasados.length }})
          </button>
        </div>

        <p v-if="!agendaVisible.length" class="vacio">
          <template v-if="verHistorial">Todavía no hay eventos pasados.</template>
          <template v-else>
            No hay eventos próximos<template v-if="puedeGestionar"> — <a href="#" @click.prevent="abrirModal()">registra el primero</a></template>.
          </template>
        </p>
        <ul v-else class="agenda">
          <li v-for="e in agendaVisible" :key="e.id_evento" class="agenda-item" :class="{ pasado: verHistorial }" @click="puedeGestionar ? abrirModal(e) : verDetalle(e)">
            <!-- En el historial no tiene sentido una cuenta regresiva negativa:
                 se dice cuánto hace que ocurrió. -->
            <div class="cuenta" :class="verHistorial ? 'u-pasado' : urgencia(e.dias_restantes)">
              <span class="cuenta-num">{{ Math.abs(e.dias_restantes) }}</span>
              <span class="cuenta-lbl">{{ verHistorial ? 'día(s) atrás' : 'día(s)' }}</span>
            </div>
            <div class="agenda-cuerpo">
              <span class="agenda-titulo">{{ e.titulo }}</span>
              <span class="agenda-meta">
                <span class="chip-tipo" :class="'t-' + colorTipo(e.tipo_evento)">{{ e.tipo_label }}</span>
                {{ fmtFecha(e.fecha_inicio) }}<template v-if="e.fecha_fin"> – {{ fmtFecha(e.fecha_fin) }}</template>
                <template v-if="e.area"> · {{ e.area.nombre }}</template>
                <template v-if="e.campus"> · {{ e.campus.nombre }}</template>
              </span>
            </div>
            <span class="estado-ev" :class="'e-' + e.estado">{{ e.estado }}</span>
          </li>
        </ul>
      </section>
    </template>

    <!-- ═══ Modal de gestión ═══ -->
    <div v-if="modal" class="overlay">
      <div class="modal">
        <h3>{{ editando ? 'Editar evento' : 'Registrar evento' }}</h3>

        <div class="form-grid">
          <label>Tipo de evento
            <select v-model="form.tipo_evento" @change="precargarRecordatorios">
              <option v-for="t in tipos" :key="t.clave" :value="t.clave">{{ t.label }}</option>
            </select>
          </label>
          <label>Título<input v-model="form.titulo" type="text" maxlength="200" placeholder="p. ej. Radicación SACES-CNA Ing. Sistemas" /></label>
          <label>Fecha inicio<input v-model="form.fecha_inicio" type="date" /></label>
          <label>Fecha fin (opcional)<input v-model="form.fecha_fin" type="date" /></label>
          <label>Programa (opcional)
            <!-- El árbol pasó de 9 a 46 nodos al sembrar el maestro SNIES, así
                 que la lista plana mezclaba programas con vicerrectorías y
                 direcciones. Agrupada por tipo, y los programas primero, que es
                 lo que se elige casi siempre. -->
            <select v-model="form.id_area"><option :value="null">— Institucional / N/A —</option>
              <optgroup v-for="g in areasAgrupadas" :key="g.tipo" :label="g.tipo">
                <option v-for="a in g.areas" :key="a.id_programa" :value="a.id_programa">{{ a.nombre }}</option>
              </optgroup>
            </select>
          </label>
          <label>Campus (opcional)
            <select v-model="form.id_campus">
              <option :value="null">— Todos los campus —</option>
              <option v-for="c in campusList" :key="c.id_campus" :value="c.id_campus">{{ c.nombre }}</option>
            </select>
          </label>
          <label>Proceso asociado (opcional)
            <select v-model="form.id_ejecucion"><option :value="null">— Ninguno —</option>
              <option v-for="p in procesos" :key="p.id" :value="p.id">{{ p.area?.nombre ?? 'Institucional' }} · {{ p.periodo }}</option>
            </select>
          </label>
        </div>

        <label class="campo-full">Descripción
          <textarea v-model="form.descripcion" rows="2" placeholder="Agenda de la visita, requisitos de la radicación…"></textarea>
        </label>

        <div class="campo-full">
          <span class="lbl">Recordatorios (días de antelación)</span>
          <div class="chips">
            <span v-for="(d, i) in form.recordatorios" :key="i" class="chip-dia">
              {{ d }} d <button class="x" @click="form.recordatorios.splice(i, 1)">×</button>
            </span>
            <input v-model.number="nuevoRecordatorio" type="number" min="0" max="730" class="chip-input" placeholder="+ días" @keyup.enter="agregarRecordatorio" />
          </div>
        </div>

        <div class="campo-full dest">
          <span class="lbl">Destinatarios de los avisos</span>
          <div class="dest-cols">
            <div>
              <span class="lbl-mini">Por rol</span>
              <label v-for="r in roles" :key="r.id" class="chk">
                <input v-model="form.destinatarios.roles" type="checkbox" :value="r.name" /> {{ r.label ?? r.name }}
              </label>
            </div>
            <div>
              <span class="lbl-mini">Por usuario</span>
              <select v-model="usuarioSeleccionado">
                <option :value="null">— Ninguno —</option>
                <option v-for="u in usuarios" :key="u.id" :value="u.id">{{ u.name }}</option>
              </select>
            </div>
          </div>
        </div>

        <p v-if="errorModal" class="aviso-error">{{ errorModal }}</p>

        <div class="modal-pie">
          <div v-if="editando" class="acciones-estado">
            <button class="btn chico" @click="cambiarEstado('confirmado')">Confirmar</button>
            <button class="btn chico ok" @click="cambiarEstado('cumplido')">Cumplido</button>
            <button class="btn chico" @click="aplazando = !aplazando">Aplazar…</button>
            <button class="btn chico peligro" @click="cambiarEstado('cancelado')">Cancelar evento</button>
          </div>
          <div v-if="aplazando" class="aplazar-linea">
            Nueva fecha: <input v-model="fechaAplazada" type="date" />
            <button class="btn chico primario" @click="aplazar">Aplazar</button>
          </div>
          <div class="botones">
            <button class="btn" @click="modal = false">Cerrar</button>
            <button class="btn primario" :disabled="guardando" @click="guardar">{{ guardando ? 'Guardando…' : 'Guardar' }}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
/**
 * Calendario de acreditación con recordatorios.
 *
 * Las alertas A1 y A6 leen estos eventos para avisar de fechas próximas y de
 * fechas pasadas sin gestionar.
 *
 * Vista enrutada. Obtiene sus datos a través de stores Pinia: no llama a `http`
 * ni a los servicios directamente.
 */

import { ref, reactive, computed, onMounted } from 'vue'
import useAutenticar from '@/store/IngresoAutenticar'
import { useDashboardFiltros } from '@/store/DashboardFiltros'
import { useGestionProcesos } from '@/store/GestionProcesoAutoevaluacion'
import { listarRoles, listarUsuarios } from '@/services/RegistroUsuariosService'
import { getProgramasService } from '@/services/ProgramaService'
import {
  getEventosService, getTiposEventoService, crearEventoService,
  actualizarEventoService, cambiarEstadoEventoService,
  type EventoCalendario, type TipoEvento, type EstadoEvento
} from '@/services/CalendarioService'
import { getCampusService } from '@/services/CampusService'
import type { Campus } from '@/interfaces/Campus'

const auth = useAutenticar()
const filtros = useDashboardFiltros()
const procesosStore = useGestionProcesos()

const puedeGestionar = computed(() => auth.tienePermiso('calendario.gestionar'))

const eventos = ref<EventoCalendario[]>([])
const tipos = ref<TipoEvento[]>([])
const roles = ref<Array<{ id: number; name: string; label?: string }>>([])
const usuarios = ref<Array<{ id?: number; name: string | null }>>([])
const areas = ref<Array<{ id_programa: number; nombre: string; tipo?: string }>>([])

/**
 * Áreas agrupadas por tipo para el selector del evento.
 *
 * Los programas van primero porque son lo que se elige casi siempre; el resto
 * del organigrama queda disponible pero sin estorbar. Un evento puede apuntar a
 * una facultad (una visita de pares, por ejemplo), así que no se filtran.
 */
const areasAgrupadas = computed(() => {
  const orden = ['programa', 'facultad', 'vicerrectoria', 'direccion', 'oficina', 'rectoria', 'presidencia']
  const etiquetas: Record<string, string> = {
    programa: 'Programas',
    facultad: 'Facultades',
    vicerrectoria: 'Vicerrectorías',
    direccion: 'Direcciones',
    oficina: 'Oficinas',
    rectoria: 'Rectoría',
    presidencia: 'Presidencia'
  }

  const porTipo = new Map<string, typeof areas.value>()
  for (const a of areas.value) {
    const tipo = a.tipo ?? 'otros'
    if (!porTipo.has(tipo)) porTipo.set(tipo, [])
    porTipo.get(tipo)!.push(a)
  }

  const claves = [...porTipo.keys()].sort((a, b) => {
    const ia = orden.indexOf(a), ib = orden.indexOf(b)
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib)
  })

  return claves.map(tipo => ({
    tipo: etiquetas[tipo] ?? tipo,
    areas: [...porTipo.get(tipo)!].sort((a, b) => a.nombre.localeCompare(b.nombre))
  }))
})
const campusList = ref<Campus[]>([])
const cargando = ref(false)
const error = ref('')

const mesRef = ref(new Date(new Date().getFullYear(), new Date().getMonth(), 1))

const LEYENDA: Record<string, string> = {
  vencimiento: 'Vencimientos', radicacion: 'Radicaciones', visita: 'Visitas de pares', otro: 'Otros'
}

function colorTipo(tipo: string): string {
  if (tipo.startsWith('vencimiento')) return 'vencimiento'
  if (tipo === 'radicacion') return 'radicacion'
  if (tipo === 'visita_pares') return 'visita'
  return 'otro'
}
function urgencia(dias: number): string {
  return dias <= 7 ? 'u-critica' : dias <= 30 ? 'u-alta' : dias <= 90 ? 'u-media' : 'u-info'
}
function fmtFecha(iso: string): string {
  const d = new Date(iso + 'T00:00:00')
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' })
}

const eventosFiltrados = computed(() =>
  filtros.calTipo ? eventos.value.filter(e => e.tipo_evento === filtros.calTipo) : eventos.value
)
const eventosFuturos = computed(() =>
  [...eventosFiltrados.value]
    .filter(e => e.dias_restantes >= 0 && ['programado', 'confirmado', 'aplazado'].includes(e.estado))
    .sort((a, b) => a.dias_restantes - b.dias_restantes)
)

/**
 * Historial: lo que ya ocurrió.
 *
 * Los eventos pasados nunca se borran —el backend los conserva y el calendario
 * mensual los sigue pintando—, pero la agenda solo listaba los pendientes, así
 * que desde aquí parecía que desaparecían. Incluye también los cerrados
 * (realizado, cancelado), que por estado tampoco salían en «próximos».
 */
const eventosPasados = computed(() =>
  [...eventosFiltrados.value]
    .filter(e => e.dias_restantes < 0 || ['realizado', 'cancelado'].includes(e.estado))
    // Lo más reciente primero: un historial se lee hacia atrás.
    .sort((a, b) => b.dias_restantes - a.dias_restantes)
)

const verHistorial = ref(false)
const agendaVisible = computed(() => (verHistorial.value ? eventosPasados.value : eventosFuturos.value))

const mesLabel = computed(() => mesRef.value.toLocaleDateString('es-CO', { month: 'long', year: 'numeric' }))
function mover(paso: number): void {
  mesRef.value = new Date(mesRef.value.getFullYear(), mesRef.value.getMonth() + paso, 1)
}

const celdas = computed(() => {
  const y = mesRef.value.getFullYear()
  const m = mesRef.value.getMonth()
  const inicio = (new Date(y, m, 1).getDay() + 6) % 7
  const diasMes = new Date(y, m + 1, 0).getDate()
  const hoyIso = new Date().toISOString().slice(0, 10)

  const porFecha: Record<string, EventoCalendario[]> = {}
  for (const e of eventosFiltrados.value) {
    (porFecha[e.fecha_inicio] ??= []).push(e)
  }

  const arr: Array<{ fecha: string | null; dia: number; esHoy: boolean; eventos: EventoCalendario[] }> = []
  for (let i = 0; i < inicio; i++) arr.push({ fecha: null, dia: 0, esHoy: false, eventos: [] })
  for (let d = 1; d <= diasMes; d++) {
    const fecha = `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
    arr.push({ fecha, dia: d, esHoy: fecha === hoyIso, eventos: porFecha[fecha] ?? [] })
  }
  return arr
})

/* ── Modal ── */
const modal = ref(false)
const editando = ref<number | null>(null)
const guardando = ref(false)
const errorModal = ref('')
const aplazando = ref(false)
const fechaAplazada = ref('')
const nuevoRecordatorio = ref<number | null>(null)

const form = reactive({
  tipo_evento: 'radicacion',
  titulo: '',
  descripcion: '' as string | null,
  id_area: null as number | null,
  id_campus: null as number | null,
  id_ejecucion: null as number | null,
  fecha_inicio: '',
  fecha_fin: '' as string | null,
  recordatorios: [] as number[],
  destinatarios: { roles: [] as string[], usuarios: [] as number[] }
})

const procesos = computed(() => procesosStore.procesos.filter(p => !!p.id))

const usuarioSeleccionado = computed({
  get: () => form.destinatarios.usuarios[0] ?? null,
  set: (v: number | null) => { form.destinatarios.usuarios = v ? [v] : [] }
})

function precargarRecordatorios(): void {
  const t = tipos.value.find(x => x.clave === form.tipo_evento)
  if (t) form.recordatorios = [...t.recordatorios_sugeridos]
}
function agregarRecordatorio(): void {
  const v = nuevoRecordatorio.value
  if (v !== null && v >= 0 && !form.recordatorios.includes(v)) {
    form.recordatorios.push(v)
    form.recordatorios.sort((a, b) => b - a)
  }
  nuevoRecordatorio.value = null
}

function abrirModal(e?: EventoCalendario): void {
  errorModal.value = ''
  aplazando.value = false
  if (e) {
    editando.value = e.id_evento
    form.tipo_evento = e.tipo_evento
    form.titulo = e.titulo
    form.descripcion = e.descripcion ?? ''
    form.id_area = e.id_area
    form.id_campus = e.id_campus
    form.id_ejecucion = e.id_ejecucion
    form.fecha_inicio = e.fecha_inicio
    form.fecha_fin = e.fecha_fin ?? ''
    form.recordatorios = [...(e.recordatorios ?? e.recordatorios_efectivos)]
    form.destinatarios = {
      roles: [...(e.destinatarios?.roles ?? [])],
      usuarios: [...(e.destinatarios?.usuarios ?? [])]
    }
  } else {
    editando.value = null
    form.titulo = ''
    form.descripcion = ''
    form.id_area = null
    form.id_campus = null
    form.id_ejecucion = null
    form.fecha_inicio = ''
    form.fecha_fin = ''
    form.destinatarios = { roles: [], usuarios: [] }
    precargarRecordatorios()
  }
  modal.value = true
}

function verDetalle(e: EventoCalendario): void {
  // Lectura sin permiso de gestión: modal solo informativo (reutiliza el mismo, sin guardar).
  abrirModal(e)
}

async function guardar(): Promise<void> {
  if (!puedeGestionar.value) { modal.value = false; return }
  errorModal.value = ''
  guardando.value = true
  const payload = {
    ...form,
    descripcion: form.descripcion || null,
    fecha_fin: form.fecha_fin || null
  }
  const r = editando.value
    ? await actualizarEventoService(editando.value, payload)
    : await crearEventoService(payload)
  guardando.value = false
  if (r.ok) {
    modal.value = false
    cargar()
  } else {
    errorModal.value = r.error ?? 'No se pudo guardar'
  }
}

async function cambiarEstado(estado: EstadoEvento): Promise<void> {
  if (!editando.value) return
  const r = await cambiarEstadoEventoService(editando.value, estado)
  if (r.ok) { modal.value = false; cargar() } else { errorModal.value = r.error ?? 'Error' }
}

async function aplazar(): Promise<void> {
  if (!editando.value || !fechaAplazada.value) return
  const r = await cambiarEstadoEventoService(editando.value, 'aplazado', fechaAplazada.value)
  if (r.ok) { modal.value = false; aplazando.value = false; cargar() } else { errorModal.value = r.error ?? 'Error' }
}

async function cargar(): Promise<void> {
  cargando.value = true
  error.value = ''
  const r = await getEventosService()
  if (r.ok && r.data) eventos.value = r.data
  else error.value = r.error ?? 'No se pudo cargar el calendario'
  cargando.value = false
}

onMounted(async () => {
  cargar()
  const t = await getTiposEventoService()
  if (t.ok && t.data) tipos.value = t.data
  if (puedeGestionar.value) {
    roles.value = await listarRoles().catch(() => [])
    usuarios.value = (await listarUsuarios().catch(() => [])).filter(u => !!u.id) as never
    try { areas.value = (await getProgramasService()) as never } catch { /* opcional */ }
    campusList.value = await getCampusService().catch(() => [])
    if (!procesosStore.procesos.length) procesosStore.cargarTodos()
  }
})
</script>

<style scoped>
.formulario-modelo {
  background-color: white;
  padding: 2rem;
  border-radius: var(--radius-lg);
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  box-shadow: var(--shadow-md);
  box-sizing: border-box;
  font-family: var(--font-family);
}

.header-flex { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; flex-wrap: wrap; margin-bottom: 1rem; }
h1 { color: var(--text-primary); font-size: 1.4rem; margin: 0; }
.sub { color: var(--text-tertiary); font-size: 0.85rem; margin: 0.25rem 0 0; }

.grilla-wrap { overflow-x: auto; }

.controles { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 0.8rem; margin: 1rem 0; }
.tabs { display: flex; border-bottom: 2px solid #e0e0e0; }
.tab { padding: 0.5rem 1.1rem; font-weight: 600; border: none; background: none; cursor: pointer; color: #666; border-bottom: 2px solid transparent; margin-bottom: -2px; font-size: 0.88rem; }
.tab.activo, .tab:hover { color: #0e69a0; }
.tab.activo { border-bottom-color: #0e69a0; }
.filtros-linea select { padding: 0.4rem 0.6rem; border: 1px solid var(--border-medium); border-radius: var(--radius-sm); font-size: 0.85rem; }

.tarjeta { background: #fff; border: 1px solid var(--border-separator); border-radius: var(--radius-lg); padding: 1rem 1.2rem; box-shadow: var(--shadow-sm); width: 100%; box-sizing: border-box; }
.vacio { color: var(--text-muted); padding: 1.5rem; text-align: center; }
.vacio a { color: #0e69a0; font-weight: 600; }

/* Mensual */
.mes-nav { display: flex; align-items: center; justify-content: center; gap: 1rem; margin-bottom: 0.8rem; }
.mes-nav h2 { margin: 0; font-size: 1.05rem; color: #0e69a0; text-transform: capitalize; min-width: 180px; text-align: center; }
.btn-nav { border: 1px solid #d9e1ea; background: #fff; color: #0e69a0; border-radius: var(--radius-sm); width: 30px; height: 30px; cursor: pointer; font-size: 1.1rem; }
.btn-nav:hover { background: #eef5fb; }
.grilla { display: grid; grid-template-columns: repeat(7, minmax(0, 1fr)); gap: 4px; min-width: 480px; width: 100%; }
.dow { text-align: center; font-size: 0.7rem; font-weight: 700; color: var(--text-disabled); padding: 4px 0; text-transform: uppercase; }
.celda { min-height: 84px; border: 1px solid #eef1f4; border-radius: var(--radius-sm); padding: 4px; display: flex; flex-direction: column; gap: 2px; overflow: hidden; }
.celda.vacia { border: none; }
.celda.hoy { border-color: #0e69a0; background: #f5faff; }
.num-dia { font-size: 0.72rem; font-weight: 700; color: #666; }
.celda.hoy .num-dia { color: #0e69a0; }
.chip-ev { border: none; border-radius: 4px; font-size: 0.66rem; padding: 2px 5px; cursor: pointer; text-align: left; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-weight: 600; }
.t-vencimiento { background: #f8d7da; color: #721c24; }
.t-radicacion { background: #ffe6c4; color: #7a4a08; }
.t-visita { background: #e8f4fd; color: #0e69a0; }
.t-otro { background: #ececec; color: #555; }

.leyenda { display: flex; gap: 1.1rem; margin-top: 0.7rem; flex-wrap: wrap; }
.leyenda-item { display: flex; align-items: center; gap: 0.35rem; font-size: 0.74rem; color: var(--text-tertiary); }
.pto { width: 10px; height: 10px; border-radius: 3px; display: inline-block; }

/* Agenda */
.agenda { list-style: none; margin: 0; padding: 0; }
.agenda-item { display: flex; align-items: center; gap: 1rem; padding: 0.7rem 0.5rem; border-bottom: 1px solid #f0f0f0; cursor: pointer; border-radius: var(--radius-sm); }
.agenda-item:hover { background: #f5f9fc; }
/* El historial se ve como lo que es: consultable, pero ya no accionable. */
.agenda-item.pasado { opacity: 0.75; }
.u-pasado { background: #eef2f6; color: #6b7280; }
.agenda-tabs { display: flex; gap: 0.4rem; margin-bottom: 0.8rem; }
.agenda-tabs button {
  border: 1px solid #d6dee6; background: white; color: #4a5b6b;
  border-radius: 999px; padding: 0.3rem 0.85rem; font-size: 0.8rem; cursor: pointer;
}
.agenda-tabs button.activo { background: #14507d; color: white; border-color: #14507d; }
.cuenta { min-width: 66px; text-align: center; border-radius: var(--radius-md); padding: 0.35rem 0.4rem; }
.cuenta-num { display: block; font-size: 1.25rem; font-weight: 800; line-height: 1.1; }
.cuenta-lbl { font-size: 0.62rem; text-transform: uppercase; letter-spacing: 0.04em; }
.u-critica { background: #f8d7da; color: #721c24; }
.u-alta { background: #fff3cd; color: #856404; }
.u-media { background: #e8f4fd; color: #0e69a0; }
.u-info { background: #f0f0f0; color: #666; }
.agenda-cuerpo { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.agenda-titulo { font-weight: 600; color: #333; font-size: 0.92rem; }
.agenda-meta { font-size: 0.76rem; color: var(--text-tertiary); display: flex; align-items: center; gap: 0.4rem; flex-wrap: wrap; margin-top: 2px; }
.chip-tipo { padding: 1px 8px; border-radius: 8px; font-size: 0.68rem; font-weight: 700; }
.estado-ev { font-size: 0.7rem; font-weight: 700; padding: 2px 10px; border-radius: var(--radius-pill); text-transform: capitalize; }
.e-programado { background: #f0f0f0; color: #555; }
.e-confirmado { background: #e8f4fd; color: #0e69a0; }
.e-aplazado { background: #fff3cd; color: #856404; }
.e-cumplido { background: #d4edda; color: #155724; }
.e-cancelado { background: #f8d7da; color: #721c24; }

/* Modal */
.overlay { position: fixed; inset: 0; background: rgba(20, 30, 40, 0.45); display: flex; align-items: flex-start; justify-content: center; z-index: 80; padding: 2rem 1rem; overflow-y: auto; }
.modal { background: #fff; border-radius: var(--radius-lg); padding: 1.8rem 2rem; width: 860px; max-width: calc(100% - 2rem); box-shadow: var(--shadow-lg); margin-bottom: 2rem; box-sizing: border-box; }
.modal h3 { margin: 0 0 1.2rem; color: #111; font-size: 1.1rem; }
.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0.9rem; }
@media (max-width: 680px) { .form-grid { grid-template-columns: 1fr; } }
label, .lbl { display: flex; flex-direction: column; font-size: 0.78rem; color: var(--text-secondary); gap: 0.25rem; font-weight: 600; min-width: 0; }
input:not([type=checkbox]):not([type=radio]), select, textarea { border: 1px solid var(--border-medium); border-radius: var(--radius-sm); padding: 0.5rem 0.65rem; font-size: 0.85rem; font-family: inherit; font-weight: 400; width: 100%; box-sizing: border-box; min-width: 0; }
input[type=checkbox], input[type=radio] { width: auto; height: auto; padding: 0; border: revert; }
.campo-full { margin-top: 0.7rem; display: flex; flex-direction: column; gap: 0.3rem; }
.chips { display: flex; flex-wrap: wrap; gap: 0.4rem; align-items: center; }
.chip-dia { background: #e8f4fd; color: #0e69a0; border-radius: var(--radius-pill); padding: 2px 8px; font-size: 0.76rem; font-weight: 700; display: inline-flex; align-items: center; gap: 4px; }
.chip-dia .x { border: none; background: none; cursor: pointer; color: #0e69a0; font-weight: 800; padding: 0; }
.chip-input { width: 76px; }
.dest-cols { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
@media (max-width: 680px) { .dest-cols { grid-template-columns: 1fr; } }
.lbl-mini { font-size: 0.72rem; color: var(--text-muted); font-weight: 700; display: block; margin-bottom: 0.3rem; }
.chk { flex-direction: row; align-items: center; gap: 0.4rem; font-weight: 400; font-size: 0.8rem; margin-bottom: 0.2rem; }

.modal-pie { margin-top: 1rem; display: flex; flex-direction: column; gap: 0.6rem; }
.acciones-estado { display: flex; gap: 0.4rem; flex-wrap: wrap; }
.aplazar-linea { display: flex; align-items: center; gap: 0.5rem; font-size: 0.8rem; }
.botones { display: flex; justify-content: flex-end; gap: 0.5rem; }

.btn { border: 1px solid #0e69a0; background: #fff; color: #0e69a0; padding: 0.45rem 1rem; border-radius: var(--radius-md); cursor: pointer; font-weight: 600; font-size: 0.85rem; transition: all 0.15s ease; }
.btn:hover { background: #0e69a0; color: #fff; }
.btn.primario { background: #eea222; border-color: #eea222; color: #222; }
.btn.primario:hover { background: #0e69a0; border-color: #0e69a0; color: #fff; }
.btn.chico { padding: 0.28rem 0.7rem; font-size: 0.76rem; }
.btn.ok { border-color: #28a745; color: #155724; }
.btn.ok:hover { background: #28a745; color: #fff; }
.btn.peligro { border-color: #dc3545; color: #dc3545; }
.btn.peligro:hover { background: #dc3545; color: #fff; }
.btn:disabled { opacity: 0.55; cursor: default; }

.aviso-error { background: var(--color-error-bg); color: var(--color-error-text); padding: 0.6rem 0.9rem; border-radius: var(--radius-md); font-size: 0.82rem; }

.skeleton-grupo { margin-top: 0.5rem; }
.skeleton { border-radius: var(--radius-lg); background: linear-gradient(90deg, #eef1f4 25%, #f7f9fb 50%, #eef1f4 75%); background-size: 200% 100%; animation: brillo 1.2s infinite; }
@keyframes brillo { from { background-position: 200% 0; } to { background-position: -200% 0; } }
</style>
